<template>
  <section v-if="authorized" class="account-access" aria-labelledby="account-access-title">
    <div class="account-access__main">
      <div class="account-access__summary">
        <span class="account-access__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
            <rect x="5" y="10" width="14" height="11" rx="2" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v3" />
          </svg>
        </span>
        <div class="account-access__copy">
          <h2 id="account-access-title" class="account-access__title">Accesso account</h2>
          <p class="account-access__identity">
            <span>{{ profile.email }}</span>
            <span v-if="eligible" class="account-access__temporary">Password temporanea: <strong>Fusco6202</strong></span>
          </p>
        </div>
      </div>
      <button
        v-if="eligible"
        ref="triggerButton" type="button" class="account-access__button"
        :disabled="busy || !canStartNew" @click="openConfirmation"
      >Reimposta password</button>
    </div>
    <p v-if="!eligible" class="account-access__notice">{{ eligibilityReason }}</p>
    <p v-if="feedback" class="account-access__feedback" :class="`account-access__feedback--${feedbackKind}`" role="status">
      {{ feedback }}
    </p>
    <button
      v-if="canVerify" type="button" class="account-access__button account-access__button--secondary"
      :disabled="busy" @click="verifyOutcome"
    >{{ busy ? 'Verifica in corso…' : 'Verifica esito' }}</button>

    <div v-if="dialogOpen" class="account-access__overlay" @keydown="handleDialogKeydown">
      <div
        ref="dialogPanel" class="account-access__dialog" role="dialog" aria-modal="true"
        aria-labelledby="password-reset-title" aria-describedby="password-reset-description" tabindex="-1"
      >
        <h3 id="password-reset-title">{{ verifyingOnly ? 'Verifica esito del reset' : 'Reimposta password' }}</h3>
        <p v-if="verifyingOnly" id="password-reset-description">
          Conferma il tuo accesso per verificare il reset della password di <strong>{{ profileName }}</strong> ({{ profile.email }}).
        </p>
        <p v-else id="password-reset-description">
          La password di <strong>{{ profileName }}</strong> ({{ profile.email }}) verrà impostata a
          <strong>Fusco6202</strong>. La password precedente smetterà di funzionare; i dati del cliente resteranno invariati.
        </p>
        <p v-if="!verifyingOnly">Comunica la password temporanea al cliente e invitalo a cambiarla dopo l’accesso.</p>
        <div v-if="reauthRequired" class="account-access__reauth">
          <template v-if="canReauthenticate">
            <label for="password-reset-admin-password">Conferma la tua password amministratore</label>
            <input
              id="password-reset-admin-password" v-model="adminPassword" type="password"
              autocomplete="current-password" :disabled="busy" @keydown.enter.prevent="confirmReset"
            >
          </template>
          <p v-else>Per confermare l’operazione, accedi di nuovo e riprova.</p>
        </div>
        <p v-if="dialogError" class="account-access__error" role="alert">{{ dialogError }}</p>
        <div class="account-access__actions">
          <button type="button" class="account-access__button account-access__button--secondary" :disabled="busy" @click="closeConfirmation">Annulla</button>
          <button
            type="button" class="account-access__button" :disabled="busy || (reauthRequired && !canReauthenticate)"
            @click="confirmReset"
          >{{ busy ? 'Operazione in corso…' : (verifyingOnly ? 'Conferma accesso e verifica' : (reauthRequired ? 'Conferma accesso e reimposta' : 'Conferma reset')) }}</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
/* global globalThis */
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import { EmailAuthProvider, onAuthStateChanged, reauthenticateWithCredential } from 'firebase/auth';
import { doc, getDocFromServer } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '@/firebaseConfig';

const CLIENT_ROLES = ['User', 'Utente', 'Cliente'];
const OWNER_UID = 'HpMiFyBZnwX6EUf6VR9iCsHkqo82';
const OWNER_EMAIL = 'valeriobianconi@gmail.com';
const normalizedEmail = value => typeof value === 'string' ? value.trim().toLowerCase() : '';
const isOwner = user => user?.uid === OWNER_UID && normalizedEmail(user.email) === OWNER_EMAIL;
const validUid = value => typeof value === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(value);
const validEmail = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) && value.length <= 320;

function createOperationId() {
  const crypto = globalThis.crypto;
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  if (typeof crypto?.getRandomValues !== 'function') throw new Error('secure-random-unavailable');
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, '0')).join('');
}

export default {
  name: 'AdminPasswordReset',
  props: { userId: { type: String, required: true } },
  setup(props) {
    const authorized = ref(false);
    const profile = ref(null);
    const targetInRoster = ref(false);
    const dialogOpen = ref(false);
    const busy = ref(false);
    const operation = ref(null);
    const feedback = ref('');
    const feedbackKind = ref('info');
    const dialogError = ref('');
    const reauthRequired = ref(false);
    const adminPassword = ref('');
    const dialogPanel = ref(null);
    const triggerButton = ref(null);
    const passwordProvider = ref(false);
    let generation = 0;
    let disposed = false;
    let observedUser = auth.currentUser;
    let verifiedActor = null;
    let attemptActor = null;
    const resetPassword = httpsCallable(functions, 'resetClientPassword');

    const eligible = computed(() => profile.value && validEmail(profile.value.email) &&
      !targetInRoster.value && props.userId !== auth.currentUser?.uid &&
      !profile.value.accountMergeOperation && !profile.value.mergedInto &&
      ([undefined, null, ''].includes(profile.value.userRole) || CLIENT_ROLES.includes(profile.value.userRole)));
    const eligibilityReason = computed(() => profile.value?.mergedInto ?
      'Questo profilo è stato unito a un altro account. Apri il profilo principale.' :
      (profile.value?.accountMergeOperation ? 'È in corso un’unione del profilo. Attendi il completamento prima del reset.' :
        'Il reset è disponibile soltanto per gli account cliente con un’email valida.'));
    const profileName = computed(() => [profile.value?.name, profile.value?.surname]
      .filter(value => typeof value === 'string' && value.trim()).join(' ').trim() || 'Cliente');
    const canReauthenticate = computed(() => passwordProvider.value);
    const canVerify = computed(() => operation.value && ['processing', 'needs-review', 'unknown'].includes(operation.value.status));
    const canStartNew = computed(() => !operation.value || operation.value.status === 'not-found');
    const verifyingOnly = computed(() => operation.value?.statusOnly === true);

    const capture = () => ({ generation, actor: auth.currentUser, uid: props.userId });
    const isCurrent = snapshot => !disposed && snapshot.generation === generation &&
      snapshot.actor === auth.currentUser && snapshot.uid === props.userId;

    function clearState() {
      generation += 1;
      authorized.value = false;
      profile.value = null;
      targetInRoster.value = false;
      dialogOpen.value = false;
      busy.value = false;
      operation.value = null;
      feedback.value = '';
      dialogError.value = '';
      reauthRequired.value = false;
      adminPassword.value = '';
      passwordProvider.value = false;
      verifiedActor = null;
      attemptActor = null;
    }

    async function loadAccess() {
      clearState();
      const snapshot = capture();
      if (!isOwner(snapshot.actor) || !validUid(snapshot.uid)) return;
      try {
        const [actor, roster, target] = await Promise.all([
          getDocFromServer(doc(db, 'users', snapshot.actor.uid)),
          getDocFromServer(doc(db, 'trainingAccess', '_staff')),
          getDocFromServer(doc(db, 'users', snapshot.uid)),
        ]);
        if (!isCurrent(snapshot)) return;
        const uids = roster.exists() && roster.data()?.uids;
        const resetAdminUids = roster.exists() && roster.data()?.passwordResetAdminUids;
        if (!actor.exists() || actor.data()?.userRole !== 'Amministratore' ||
          !Array.isArray(uids) || !uids.includes(snapshot.actor.uid) ||
          !Array.isArray(resetAdminUids) || !resetAdminUids.includes(snapshot.actor.uid) || !target.exists()) return;
        const data = target.data();
        profile.value = { name: data.name, surname: data.surname,
          email: normalizedEmail(data.email), userRole: data.userRole,
          accountMergeOperation: data.accountMergeOperation, mergedInto: data.mergedInto };
        targetInRoster.value = uids.includes(snapshot.uid);
        passwordProvider.value = Array.isArray(snapshot.actor.providerData) &&
          snapshot.actor.providerData.some(provider => provider.providerId === 'password');
        verifiedActor = snapshot.actor;
        authorized.value = true;
      } catch {
        // This control stays hidden when authorization cannot be verified.
      }
    }

    async function openConfirmation() {
      if (!isOwner(auth.currentUser) || !authorized.value || !eligible.value || busy.value || !canStartNew.value) return;
      if (verifiedActor !== auth.currentUser) { loadAccess(); return; }
      operation.value = null;
      attemptActor = null;
      dialogError.value = '';
      reauthRequired.value = false;
      adminPassword.value = '';
      dialogOpen.value = true;
      await nextTick();
      dialogPanel.value?.focus();
    }

    function closeConfirmation() {
      if (busy.value) return;
      dialogOpen.value = false;
      adminPassword.value = '';
      dialogError.value = '';
      reauthRequired.value = false;
      // A rejected recent-login check has not sent a password write.
      if (operation.value?.status === 'reauth-required') {
        if (operation.value.statusOnly) operation.value = { ...operation.value, status: 'unknown' };
        else { operation.value = null; attemptActor = null; }
      }
      nextTick(() => triggerButton.value?.focus());
    }

    function handleDialogKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeConfirmation();
      }
      if (event.key !== 'Tab') return;
      const controls = Array.from(dialogPanel.value?.querySelectorAll('button:not([disabled]), input:not([disabled])') || []);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); return; }
      const active = globalThis.document?.activeElement;
      if (event.shiftKey && (active === first || active === dialogPanel.value)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (active === last || active === dialogPanel.value)) {
        event.preventDefault(); first.focus();
      }
    }

    function reportResult(result, pending) {
      if (!result || result.operationId !== pending.operationId || result.targetUid !== pending.uid ||
        normalizedEmail(result.email) !== pending.expectedEmail ||
        !['completed', 'processing', 'needs-review', 'not-found'].includes(result.status) ||
        (result.status === 'not-found' && !pending.statusOnly)) {
        throw new Error('invalid-reset-result');
      }
      operation.value = { ...pending, status: result.status };
      feedbackKind.value = result.status === 'completed' && !result.auditPending ? 'success' : 'info';
      if (result.status === 'not-found') {
        feedback.value = 'Nessuna operazione registrata. Puoi riprovare con una nuova conferma del reset.';
      } else if (result.status === 'completed') {
        feedback.value = pending.statusOnly ?
          'L’operazione di reset risulta completata. Se il cliente ha cambiato la password in seguito, resta valida quella scelta dal cliente.' : result.auditPending ?
          'Password reimpostata a Fusco6202. La registrazione dell’operazione è ancora in attesa di verifica.' :
          'Password reimpostata a Fusco6202. Il cliente può accedere con la password temporanea e cambiarla.';
      } else if (result.status === 'processing') {
        feedback.value = 'Il reset è ancora in corso. Usa “Verifica esito” per controllare il risultato.';
      } else {
        feedback.value = 'L’esito del reset richiede una verifica. Controlla l’esito prima di procedere.';
      }
      dialogOpen.value = false;
      reauthRequired.value = false;
      adminPassword.value = '';
    }

    function reportError(error, pending) {
      const reason = error?.details?.reason;
      if (reason === 'recent-login-required') {
        operation.value = { ...pending, status: 'reauth-required' };
        reauthRequired.value = true;
        dialogOpen.value = true;
        dialogError.value = canReauthenticate.value ?
          'Conferma il tuo accesso per completare il reset.' : 'Accedi di nuovo e riprova.';
        return;
      }
      const messages = {
        'administrator-required': 'Il tuo account non è autorizzato a reimpostare le password.',
        'account-owner-required': 'Questo comando è riservato all’account autorizzato.',
        'client-account-required': 'Questo comando è disponibile soltanto per gli account cliente.',
        'identity-mismatch': 'L’email dell’account è cambiata o non corrisponde al profilo. Riapri il profilo prima di procedere.',
        'target-account-merging': 'Il profilo è in fase di unione o è già stato unito. Apri il profilo principale prima del reset.',
        'target-disabled': 'L’account è disabilitato. La password non è stata modificata.',
        'password-provider-required': 'Questo account non utilizza l’accesso con password.',
        'target-not-found': 'L’account di accesso non è stato trovato.',
        'reset-pending-review': 'Un reset precedente richiede una verifica. Non avviare altri reset per questo account.',
        'cooldown': 'La password è stata appena reimpostata. Attendi prima di ripetere il reset.',
        'operation-aborted': 'Il reset è stato annullato prima della modifica. Riapri il profilo prima di procedere.',
        'operation-conflict': 'Questa operazione non può essere completata. Riapri il profilo.',
        'invalid-session': 'La sessione non è più valida. Accedi di nuovo e riprova.',
        'sign-in-required': 'Accedi di nuovo e riprova.',
      };
      const knownRejection = messages[reason] || ['functions/invalid-argument', 'functions/permission-denied', 'functions/not-found', 'functions/unauthenticated'].includes(error?.code);
      operation.value = { ...pending, status: knownRejection ? 'blocked' : 'unknown' };
      feedbackKind.value = 'info';
      feedback.value = messages[reason] || (knownRejection ? 'Il reset non è stato autorizzato. Riapri il profilo prima di procedere.' :
        'Non è stato possibile confermare il risultato. Usa “Verifica esito” prima di ripetere l’operazione.');
      dialogOpen.value = false;
      reauthRequired.value = false;
    }

    async function invokeReset(statusOnly = operation.value?.statusOnly === true) {
      const snapshot = capture();
      const pending = operation.value && { ...operation.value, statusOnly };
      if (verifiedActor !== snapshot.actor || attemptActor !== snapshot.actor) { loadAccess(); return; }
      if (!isCurrent(snapshot) || !isOwner(snapshot.actor) || !pending || pending.actorUid !== snapshot.actor.uid ||
        pending.uid !== snapshot.uid || !authorized.value || (!statusOnly && !eligible.value) || busy.value) return;
      busy.value = true;
      dialogError.value = '';
      try {
        if (reauthRequired.value) {
          if (!canReauthenticate.value || !adminPassword.value) {
            dialogError.value = 'Inserisci la tua password amministratore.';
            return;
          }
          const password = adminPassword.value;
          adminPassword.value = '';
          try {
            await reauthenticateWithCredential(snapshot.actor, EmailAuthProvider.credential(snapshot.actor.email, password));
            if (!isCurrent(snapshot)) return;
            await snapshot.actor.getIdToken(true);
          } catch {
            if (isCurrent(snapshot)) dialogError.value = 'Accesso non confermato. Controlla la tua password e riprova.';
            return;
          }
        }
        if (!isCurrent(snapshot)) return;
        const payload = { uid: pending.uid, expectedEmail: pending.expectedEmail, operationId: pending.operationId };
        if (pending.statusOnly) payload.statusOnly = true;
        const response = await resetPassword(payload);
        if (isCurrent(snapshot)) reportResult(response.data, pending);
      } catch (error) {
        if (isCurrent(snapshot)) reportError(error, pending);
      } finally {
        if (isCurrent(snapshot)) { busy.value = false; adminPassword.value = ''; }
      }
    }

    async function confirmReset() {
      if (!isOwner(auth.currentUser) || !dialogOpen.value || !authorized.value || (!verifyingOnly.value && !eligible.value) || busy.value) return;
      if (verifiedActor !== auth.currentUser) { loadAccess(); return; }
      if (!operation.value) {
        try {
          attemptActor = auth.currentUser;
          operation.value = { uid: props.userId, expectedEmail: profile.value.email,
            operationId: createOperationId(), actorUid: auth.currentUser.uid, status: 'pending' };
        } catch {
          dialogError.value = 'Non è stato possibile preparare il reset. Ricarica la pagina e riprova.';
          return;
        }
      }
      await invokeReset();
    }

    async function verifyOutcome() {
      if (!canVerify.value || busy.value) return;
      await invokeReset(true);
    }

    watch(() => props.userId, loadAccess, { immediate: true, flush: 'sync' });
    const stopAuth = onAuthStateChanged(auth, user => {
      if (user === observedUser) return;
      observedUser = user;
      loadAccess();
    });
    onUnmounted(() => {
      disposed = true;
      clearState();
      stopAuth();
    });

    return { authorized, profile, profileName, eligible, eligibilityReason, dialogOpen, dialogPanel, triggerButton,
      busy, operation, feedback, feedbackKind, dialogError, reauthRequired, adminPassword,
      canReauthenticate, canVerify, canStartNew, verifyingOnly, openConfirmation, closeConfirmation, confirmReset, verifyOutcome, handleDialogKeydown };
  },
};
</script>

<style scoped>
.account-access { margin: 12px 0 18px; padding: 12px 14px; border: 1px solid #e1e3e6; border-radius: 10px; background: #f8f9fa; color: #262626; }
.account-access__main { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.account-access__summary { display: flex; align-items: center; gap: 10px; min-width: 0; }
.account-access__icon { display: grid; place-items: center; width: 34px; height: 34px; flex-shrink: 0; border: 1px solid #e1e3e6; border-radius: 8px; background: #fff; color: #525252; }
.account-access__icon svg { width: 19px; height: 19px; }
.account-access__copy { min-width: 0; }
#account-access-title.account-access__title { margin: 0; color: #262626; text-align: left; font-size: 14px; line-height: 20px; font-weight: 650; }
.account-access__identity { display: flex; flex-wrap: wrap; column-gap: 12px; row-gap: 2px; margin: 2px 0 0; color: #61666d; font-size: 12px; line-height: 18px; overflow-wrap: anywhere; }
.account-access__temporary { color: #61666d; }
.account-access__temporary strong { color: #343434; font-weight: 600; }
.account-access__notice { margin: 8px 0 0; font-size: 12px; line-height: 18px; color: #61666d; }
.account-access__button { flex-shrink: 0; min-height: 36px; padding: 7px 12px; border: 1px solid #262626; border-radius: 7px; background: #262626; color: #fff; font-family: inherit; font-size: 13px; line-height: 20px; font-weight: 500; cursor: pointer; }
.account-access__button:hover:not(:disabled) { background: #404040; }
.account-access__button--secondary { background: #fff; color: #222; }
.account-access__button--secondary:hover:not(:disabled) { background: #f3f3f3; }
.account-access__button:disabled { opacity: .55; cursor: not-allowed; }
.account-access__button:focus-visible, .account-access input:focus-visible { outline: 3px solid #2879dd; outline-offset: 3px; }
.account-access__feedback { margin: 10px 0 0; padding: 8px 10px; border-radius: 7px; background: #fff6df; color: #68531f; font-size: 12px; line-height: 18px; }
.account-access__feedback--success { background: #e5f7eb; color: #23663b; }
.account-access__feedback + .account-access__button { margin-top: 8px; }
.account-access__overlay { position: fixed; inset: 0; z-index: 1200; display: flex; align-items: center; justify-content: center; padding: 20px; background: rgba(0, 0, 0, .45); }
.account-access__dialog { width: min(100%, 520px); max-height: 90vh; overflow-y: auto; padding: 24px; border-radius: 12px; background: #fff; color: #262626; font-size: 14px; box-shadow: 0 10px 40px rgba(0, 0, 0, .2); }
.account-access__dialog h3 { margin-top: 0; }
.account-access__dialog p { line-height: 1.5; }
.account-access__actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 12px; margin-top: 20px; }
.account-access__reauth label { display: block; margin-bottom: 8px; }
.account-access__reauth input { box-sizing: border-box; width: 100%; padding: 10px; border: 1px solid #999; border-radius: 6px; font: inherit; }
.account-access__error { color: #ad1d1d; }
@media (max-width: 600px) {
  .account-access__main { align-items: flex-start; flex-wrap: wrap; gap: 10px; }
  .account-access__main > .account-access__button { margin-left: 44px; }
}
</style>
